import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, CreditCard, Swords, Settings, LogOut, ExternalLink, Users, Skull } from 'lucide-react';
import { motion } from 'framer-motion';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const NAV = [
  { to: '/dashboard', exact: true, icon: LayoutDashboard, label: 'Overview', testid: 'nav-overview' },
  { to: '/dashboard/card', icon: CreditCard, label: 'Membership Card', testid: 'nav-card' },
  { to: '/dashboard/history', icon: Swords, label: 'Match History', testid: 'nav-history' },
  { to: '/dashboard/network', icon: Users, label: 'Network', testid: 'nav-network' },
  { to: '/dashboard/settings', icon: Settings, label: 'Settings', testid: 'nav-settings' },
];

/**
 * Two-column dashboard shell with refined design tokens.
 * Soft warm dark surfaces, 10px corners on cards, 6px on buttons, breathing room.
 */
export default function DashboardLayout({ children, title, subtitle, actions }) {
  const navigate = useNavigate();
  const { gamer, logoutGamer } = useMafiaAuth();
  const avatar = gamer?.custom_avatar || gamer?.picture;

  return (
    <div className="ms-page" data-testid="dashboard-layout">
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-screen">
        {/* Sidebar */}
        <aside
          className="lg:sticky lg:top-0 lg:h-screen flex flex-col"
          style={{ backgroundColor: 'var(--ms-surface-1)', borderRight: '1px solid var(--ms-border-subtle)' }}
        >
          {/* Brand */}
          <button
            onClick={() => navigate('/mafia')}
            className="px-5 h-16 ms-border-b flex items-center gap-3 hover:bg-[var(--ms-surface-2)] transition-colors"
            data-testid="sidebar-brand"
          >
            <div
              className="w-8 h-8 flex items-center justify-center"
              style={{
                background: 'var(--ms-red-soft)',
                border: '1px solid rgba(220,38,38,0.35)',
                borderRadius: 'var(--ms-radius-sm)',
              }}
            >
              <Skull size={14} className="ms-text-red" />
            </div>
            <div className="text-left leading-tight">
              <p className="ms-display text-sm uppercase tracking-wide">Mafia Salon</p>
              <p className="text-[10px] ms-text-3 mt-0.5">Vangard Club</p>
            </div>
          </button>

          {/* Profile chip */}
          {gamer && (
            <button
              onClick={() => navigate(`/p/${gamer.member_no}`)}
              className="mx-3 mt-4 p-3 flex items-center gap-3 ms-border ms-card-hover text-left"
              style={{ borderRadius: 'var(--ms-radius-md)' }}
              data-testid="sidebar-profile-chip"
            >
              {avatar ? (
                <img
                  src={avatar}
                  alt={gamer.name}
                  className="w-10 h-10 object-cover"
                  style={{ borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-strong)' }}
                />
              ) : (
                <div
                  className="w-10 h-10 flex items-center justify-center font-semibold ms-text-red"
                  style={{ background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-strong)' }}
                >
                  {(gamer.name || '?').slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold ms-text truncate">{gamer.name}</p>
                <p className="text-[11px] ms-text-3 truncate mt-0.5">{gamer.member_no}</p>
              </div>
            </button>
          )}

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-1" aria-label="Dashboard">
            <p className="ms-kicker px-3 mb-2">Menu</p>
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? 'ms-text' : 'ms-text-2 hover:ms-text'
                  }`
                }
                style={({ isActive }) => isActive
                  ? { background: 'var(--ms-surface-2)', borderRadius: 'var(--ms-radius-md)', border: '1px solid var(--ms-border-subtle)' }
                  : { borderRadius: 'var(--ms-radius-md)' }
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-bar"
                        className="absolute left-0 top-1.5 bottom-1.5 w-0.5"
                        style={{ background: 'var(--ms-red)', borderRadius: '2px' }}
                      />
                    )}
                    <item.icon size={15} className={isActive ? 'ms-text-red' : ''} />
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Bottom links */}
          <div className="px-3 py-4 ms-border-t space-y-1">
            {gamer && (
              <button
                onClick={() => navigate(`/p/${gamer.member_no}`)}
                data-testid="nav-public-profile"
                className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium ms-text-2 hover:ms-text hover:bg-[var(--ms-surface-2)] transition-colors"
                style={{ borderRadius: 'var(--ms-radius-md)' }}
              >
                <ExternalLink size={13} /> View public profile
              </button>
            )}
            <button
              onClick={async () => { await logoutGamer(); navigate('/mafia'); }}
              data-testid="nav-signout"
              className="w-full flex items-center gap-3 px-3 py-2 text-xs font-medium ms-text-2 hover:text-rose-400 hover:bg-[var(--ms-surface-2)] transition-colors"
              style={{ borderRadius: 'var(--ms-radius-md)' }}
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex flex-col">
          <header
            className="sticky top-0 z-10 backdrop-blur-xl"
            style={{ background: 'rgba(12,10,9,0.75)', borderBottom: '1px solid var(--ms-border-subtle)' }}
          >
            <div className="px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
              <div className="min-w-0">
                {subtitle && <p className="ms-kicker ms-kicker-red truncate">{subtitle}</p>}
                <h1 className="ms-display text-xl sm:text-2xl mt-0.5 truncate">{title}</h1>
              </div>
              {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
            </div>
          </header>
          <motion.div
            className="px-6 lg:px-10 py-8 flex-1"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
